import {React, useEffect, useState} from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate, useParams, Outlet } from 'react-router-dom';

import './Dashboard.css';
import Navbar from '../Navigation/Navbar';
import Introduction from '../Introduction/Introduction';
import Tos from '../PopUp/Tos';
import SpecializationSelector from '../PopUp/SpecializationSelector';
import ModalEditorHistory from '../Modal/ModalEditorHistory';

import { isAuthenticated, getCurrentUser, updateIntroduced } from '../../api/users_institutions';
import { getUserSpecializations } from '../../api/users_institutions';
import { getPersistedEditorMode } from '../../utils/editorMode';

// Shared shell for the whole /dashboard route family: Navbar, the account-level onboarding
// gates (ToS/specialization/introduction -- these should block access whether you're looking at
// the level grid or an individual level, not just the grid), and the editor-history modal all
// live here once instead of being duplicated per child. The grid (index route) and an individual
// level (:levelName route) render into the <Outlet/> below and swap without this shell
// remounting, which is what makes clicking a level-card feel instant instead of a full page
// transition.
function Dashboard() {
  const navigate = useNavigate();
  const { levelName } = useParams();
  const userIsAuthenticated = isAuthenticated();
  const currentUser = getCurrentUser();

  const [Introduced, setIntroduced] = useState(Boolean(currentUser?.introduction_completed));
  const [tosAccepted, setTosAccepted] = useState(Boolean(currentUser?.tos_accepted));
  const [showSpecializationSelector, setShowSpecializationSelector] = useState(false);
  const [userSpecializations, setUserSpecializations] = useState([]);

  const [editorMode, setEditorMode] = useState(() => getPersistedEditorMode());
  const updateEditorMode = (mode) => {
    setEditorMode(mode);
  };

  const [isModalHistoryOpen, setIsModalHistoryOpen] = useState(false);
  // Reported up by Level.jsx once it resolves the level it's showing, so the shared history
  // modal can request that specific level's logs instead of the dashboard's.
  const [activeLevelId, setActiveLevelId] = useState(null);
  // Reported up by Level.jsx's own effect (mirrors setActiveLevelId) so the Navbar's Save/Undo/
  // Redo buttons can drive Level's local editing session -- null whenever no level is mounted or
  // reporting in (e.g. on the grid), which is what keeps editorModeLite's controls correctly
  // disabled/hidden there.
  const [sortSessionControls, setSortSessionControls] = useState(null);
  // Reported up by Level.jsx once its editor-permission check resolves: admins/superadmins can
  // open a level whose category isn't aimed at "admins", but only to read it. Handed to the
  // Navbar, which is the thing that owns the editor toggle -- Level has no Navbar of its own.
  const [levelViewOnly, setLevelViewOnly] = useState(false);

  useEffect(() => {
    if (!levelName) {
      setSortSessionControls(null);
      setLevelViewOnly(false);
    }
  }, [levelName]);

  const fetchUserSpecializations = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token found in localStorage');
      return;
    }

    try {
      const specializations = await getUserSpecializations(currentUser.id);
      setUserSpecializations(specializations);
    } catch (error) {
      console.error('Error fetching user specializations:', error);
    }
  };

  const handleIntroductionComplete = async (e) => {
    setIntroduced(e);
    try {
      await updateIntroduced(e);
    } catch (error) {
      console.error('Failed to persist introduction completion:', error);
    }
  };

  useEffect(() => {
    if (!userIsAuthenticated) {
      navigate('/');
      return;
    }
    fetchUserSpecializations();
  }, [userIsAuthenticated]);

  const openHistoryModal = () => {
    setIsModalHistoryOpen(true);
  };

  const closeHistoryModal = () => {
    setIsModalHistoryOpen(false);
  };

  return (
    <>
      {userIsAuthenticated &&
        <>
          {/* .Dashboard centers its children via flexbox, which suits the grid (whose content
              wrapper opts in with width:100%) but not Level -- Level's own top-level wrapper has
              no explicit width, so under flex centering it shrinks to its content's width instead
              of filling the row, showing up as large left/right gaps. The level modifier class
              switches to block layout only while a level is active. */}
          <div className={`Dashboard${levelName ? ' Dashboard--level' : ''}`}>
            <Navbar
              updateEditorMode={(e) => updateEditorMode(e)}
              historyLogView={editorMode && !levelName}
              editorModeLite={editorMode && Boolean(levelName)}
              onSave={sortSessionControls?.onSave}
              onUndo={sortSessionControls?.onUndo}
              onRedo={sortSessionControls?.onRedo}
              canUndo={!!sortSessionControls?.canUndo}
              canRedo={!!sortSessionControls?.canRedo}
              onHistory={openHistoryModal}
              viewOnlyMode={levelViewOnly}
            />
            <ModalEditorHistory
              {...(levelName ? { levelId: activeLevelId } : { dashboard: true })}
              isOpen={isModalHistoryOpen}
              closeModal={closeHistoryModal}
            />
            <Outlet context={{ editorMode, updateEditorMode, userSpecializations, setActiveLevelId, setSortSessionControls, setLevelViewOnly }} />
          </div>
          <AnimatePresence>
            {!tosAccepted ? (
              <Tos closeTosPopup={() => { setTosAccepted(true); setShowSpecializationSelector(true); }} />
            ) : showSpecializationSelector ? (
              <SpecializationSelector onClose={() => { setShowSpecializationSelector(false); fetchUserSpecializations(); }} />
            ) : (
              !Introduced && <Introduction setIntroduced={(e) => handleIntroductionComplete(e)}/>
            )}
          </AnimatePresence>
        </>
      }
    </>
  );
}

export default Dashboard;
